import { pathOr } from 'ramda';
import { combineEpics, StateObservable } from 'redux-observable';
import { Observable, timer } from 'rxjs';
import {
  filter,
  first,
  map,
  switchMap,
} from 'rxjs/operators';

import { CANCEL_ROUND, INCIDENT_CLOSED, INCIDENT_OPEN } from '@ezugi/constants';
import { DeviceInfo } from '@ezugi/utils';

import { INCIDENT } from '../../../constants/strings';
import { dialogActions, socketActions } from '../../actions';
import { GENERIC_DIALOG } from '../../constants/dialog';
import { dialogDataSelector, incidentDialogScreenTimeSelector, localeSelector } from '../../selectors';
import { retailModeSelector } from '../../selectors/app';
import { RootState } from '../../types';
import { incidentDialogClassName, incidentDialogDesktopClassName } from './style';

// {
//   type: 'socket/message',
//   payload: {
//     MessageType: 'INCIDENT_OPEN',
//   }
// }
// {
//   type: 'socket/message',
//   payload: {
//     MessageType: 'INCIDENT_CLOSED',
//   }
// }
// {
//   type: 'socket/message',
//   payload: {
//     MessageType: 'CANCEL_ROUND',
//   }
// }

const { dialog } = dialogActions;
const { socket } = socketActions;

const ROUND_CANCELED_DESCRIPTION_KEY = 'messages.default_pui_message_cancel_round';

function incidentOpenEpic(action$: Observable<any>, state$: StateObservable<RootState>): Observable<any> {
  return action$.pipe(
    filter(((a) => socket.message.match(a))),
    map(({ payload }) => payload),
    filter(({ MessageType }) => MessageType === INCIDENT_OPEN),
    switchMap((payload) => state$.pipe(first(), map((state) => ({ locale: localeSelector(state), payload })))),
    map(({ locale }) => dialog.add({
      name: GENERIC_DIALOG,
      type: INCIDENT,
      title: locale.messages.incident_open_title ?? 'Incident open',
      html: locale.messages.incident_open_description
        ?? `Ladies and gentlemen we apologise for the inconvenience.<br/>
        Please allow us some time to check the video records.<br/>
        Thank you!`,
      className: DeviceInfo.isHandheld ? incidentDialogClassName : incidentDialogDesktopClassName,
      buttons: [ {
        action: 'continue',
        props: {
          label: locale.general.ok ?? 'Ok',
          accent: true,
        },
      } ],
    }),),
  );
}

function incidentClosedEpic(action$: Observable<any>, state$: StateObservable<RootState>): Observable<any> {
  return action$.pipe(
    filter(((a) => socket.message.match(a))),
    map(({ payload }) => payload),
    filter(({ MessageType }) => MessageType === INCIDENT_CLOSED),
    switchMap(({ LocaleKey }) => state$.pipe(
      first(),
      map((state) => ({ locale: localeSelector(state), LocaleKey }))
    )),
    map(({ locale, LocaleKey }) => {
      const hasBackOfficeResponded = LocaleKey && LocaleKey.MessageKey;
      const fallback = `Dear players, the game round is not affected and it can continue normally. <br/>
       For more details, please contact our Support through chat. <br/>
       We apologize for the inconvenience.`;

      const dialogDescription = hasBackOfficeResponded
        ? pathOr(fallback, LocaleKey.MessageKey.split('.'), locale)
        : fallback;

      return dialog.add({
        name: GENERIC_DIALOG,
        type: INCIDENT,
        title: locale.messages.incident_closed_title ?? 'Incident resolved',
        html: dialogDescription,
        className: DeviceInfo.isHandheld ? incidentDialogClassName : incidentDialogDesktopClassName,
        buttons: [ {
          action: 'continue',
          props: {
            label: locale.general.ok ?? 'Ok',
            accent: true,
          },
        } ],
      });
    })
  );
}

function roundCancelledEpic(action$: Observable<any>, state$: StateObservable<RootState>): Observable<any> {
  return action$.pipe(
    filter(((a) => socket.message.match(a))),
    map(({ payload }) => payload),
    filter(({ MessageType }) => MessageType === CANCEL_ROUND && !retailModeSelector(state$.value)),
    switchMap(({ LocaleKey }) => state$.pipe(
      first(),
      map((state) => ({ locale: localeSelector(state), LocaleKey }))
    )),
    map(({ locale, LocaleKey }) => {
      const fallbackGameModeDescription = !retailModeSelector(state$.value)
        ? `Your initial bets are refunded to your accounts automatically. <br />
        If you have any questions, please contact Support through the chat at any moment. <br/>`
        : '';

      // This is a fallback message for the case when the back office has not responded or the localization key is not found.
      // If the game is in retail mode, the fallbackGameModeDescription will be empty.
      const fallbackDescription = `Dear Players, the game round was cancelled. <br/>
        ${fallbackGameModeDescription}We apologize for the inconvenience.`;

      const roundCancelKey = LocaleKey?.MessageKey ?? ROUND_CANCELED_DESCRIPTION_KEY;

      const chunks = roundCancelKey.split('.');
      const dialogDescription = pathOr(fallbackDescription, chunks, locale);

      return dialog.add({
        name: GENERIC_DIALOG,
        type: INCIDENT,
        title: locale.messages.round_cancelled_title ?? 'Round canceled',
        html: dialogDescription,
        className: DeviceInfo.isHandheld ? incidentDialogClassName : incidentDialogDesktopClassName,
        buttons: [ {
          action: 'continue',
          props: {
            label: locale.general.ok ?? 'Ok',
            accent: true,
          },
        } ],
      });
    })
  );
}

function autoClosedIncidentDialogEpic(
  action$: Observable<any>,
  state$: StateObservable<RootState>
): Observable<any> {
  return action$.pipe(
    filter(((a) => socket.message.match(a))),
    map(({ payload }) => payload),
    filter(({ MessageType }) => [ INCIDENT_CLOSED, INCIDENT_OPEN, CANCEL_ROUND ].includes(MessageType)),
    switchMap(() => {
      const dueTime = incidentDialogScreenTimeSelector(state$.value);
      return timer(dueTime);
    }),
    filter(() => dialogDataSelector(state$.value)?.type === INCIDENT),
    map((_a) => dialog.remove()),
  );
}

export default combineEpics(
  incidentOpenEpic,
  incidentClosedEpic,
  roundCancelledEpic,
  autoClosedIncidentDialogEpic
);
